import { supabase } from '../lib/supabase';

/**
 * Service for managing career plan data persistence
 */

/**
 * Save career plan to database
 * @param {string} userId - The user ID
 * @param {Object} planData - The career plan data
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function saveCareerPlan(userId, planData, options = {}) {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    if (!planData) {
      throw new Error('Plan data is required');
    }

    const role = options.role || 'free';

    // Free tier enforcement helpers
    const ENFORCE_SHORT_TERM = (data) => {
      const safe = { ...data };
      const weeks = Number(safe?.estimatedWeeks) || Number(safe?.plan_data?.estimatedWeeks) || 0;
      const clampedWeeks = Math.max(1, Math.min(4, weeks || 4));
      safe.estimatedWeeks = clampedWeeks;
      safe.plan_data = {
        ...(safe.plan_data || {}),
        estimatedWeeks: clampedWeeks,
        tier: 'free',
        enforcedShortTerm: true
      };
      // Optionally trim very long sections if present
      if (Array.isArray(safe.sections)) {
        safe.sections = safe.sections.map((s) => ({ ...s }));
      }
      return safe;
    };

    const STRIP_FREE_ONLY_SECTIONS = (data) => {
      const safe = { ...data };
      if (Array.isArray(safe.sections)) {
        safe.sections = safe.sections.filter((s) => String(s?.type).toLowerCase() !== 'kpis');
      }
      if (safe.plan_data && Array.isArray(safe.plan_data.sections)) {
        safe.plan_data.sections = safe.plan_data.sections.filter((s) => String(s?.type).toLowerCase() !== 'kpis');
      }
      return safe;
    };

    // Prepare the plan data for database storage
    let preparedPlan = { ...planData };
    if (role === 'free') {
      preparedPlan = ENFORCE_SHORT_TERM(preparedPlan);
      preparedPlan = STRIP_FREE_ONLY_SECTIONS(preparedPlan);
    }

    const careerPlanRecord = {
      user_id: userId,
      plan_data: {
        occupation: preparedPlan.occupation || '',
        seniority_level: preparedPlan.seniority || '',
        industry: preparedPlan.industry || 'Technology',
        estimatedWeeks: preparedPlan.estimatedWeeks || 0,
        estimatedHoursPerWeek: preparedPlan.estimatedHoursPerWeek || '',
        totalExercises: preparedPlan.totalExercises || 0,
        sections: preparedPlan.sections || [],
        createdAt: preparedPlan.createdAt || new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        isSaved: true,
        regenerationCount: preparedPlan.regenerationCount || 0,
        feedback: preparedPlan.feedback || null
      },
      is_active: true
    };

    // Check if a plan already exists for this user (active)
    const { data: existingPlan, error: fetchError } = await supabase
      .from('career_plans')
      .select('id, created_at')
      .eq('user_id', userId)
      .eq('is_active', true)
      .single();

    let result;
    if (existingPlan) {
      // Update existing plan
      const { data, error } = await supabase
        .from('career_plans')
        .update(careerPlanRecord)
        .eq('id', existingPlan.id)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update career plan: ${error.message}`);
      }
      result = data;
    } else {
      // Free tier: enforce 1 plan per month when creating a new one
      if (role === 'free') {
        const since = new Date();
        since.setDate(since.getDate() - 30);
        const { count, error: countError } = await supabase
          .from('career_plans')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .gte('created_at', since.toISOString());
        if (!countError && (count ?? 0) > 0) {
          throw new Error('FREE_TIER_MONTHLY_LIMIT: You can generate 1 AI plan per month on the free tier.');
        }
      }
      // Create new plan
      const { data, error } = await supabase
        .from('career_plans')
        .insert([careerPlanRecord])
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to create career plan: ${error.message}`);
      }
      result = data;
    }

    return {
      success: true,
      data: result
    };

  } catch (error) {
    console.error('Error saving career plan:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Load career plan from database
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function loadCareerPlan(userId) {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const { data, error } = await supabase
      .from('career_plans')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 is "not found" error
      throw new Error(`Failed to load career plan: ${error.message}`);
    }

    if (!data) {
      return {
        success: true,
        data: null
      };
    }

    // Transform the database record back to the expected format
    const planData = {
      id: data.id,
      occupation: data.plan_data?.occupation || '',
      seniority: data.plan_data?.seniority_level || '',
      industry: data.plan_data?.industry || 'Technology',
      estimatedWeeks: data.plan_data?.estimatedWeeks || 0,
      estimatedHoursPerWeek: data.plan_data?.estimatedHoursPerWeek || '',
      totalExercises: data.plan_data?.totalExercises || 0,
      sections: data.plan_data?.sections || [],
      createdAt: data.plan_data?.createdAt || data.created_at,
      lastUpdated: data.plan_data?.lastUpdated || data.updated_at,
      isSaved: data.plan_data?.isSaved || true,
      regenerationCount: data.plan_data?.regenerationCount || 0,
      feedback: data.plan_data?.feedback || null
    };

    return {
      success: true,
      data: planData
    };

  } catch (error) {
    console.error('Error loading career plan:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Delete career plan from database
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deleteCareerPlan(userId) {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const { error } = await supabase
      .from('career_plans')
      .update({ is_active: false })
      .eq('user_id', userId);

    if (error) {
      throw new Error(`Failed to delete career plan: ${error.message}`);
    }

    return {
      success: true
    };

  } catch (error) {
    console.error('Error deleting career plan:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Get user's career plan history
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export async function getCareerPlanHistory(userId) {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const { data, error } = await supabase
      .from('career_plans')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch career plan history: ${error.message}`);
    }

    return {
      success: true,
      data: data || []
    };

  } catch (error) {
    console.error('Error fetching career plan history:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

